import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import extractChatMessages from "../helper-methods/fetchAndRefreshMessages";
import useStore from "../state-files/zustondStoreStates";
import HeaderBar from "../Components/Static/HeaderBar";
import ChatHeader from "../Components/Chat/ChatHeader";
import MobileUserListComponent from "../Components/Chat/MobileUserListComponent";
import InputBoxComponent from "../Components/Chat/InputBoxComponent";
import MobileEmojiStack from "../Components/Chat/MobileEmojiStack";
import DesktopUserListComponent from "../Components/Chat/DesktopUserListComponent";
import ChatMessage from "../Components/Chat/ChatMessage";
import Sidebar from "../Components/Sidebar/Sidebar";
import MobileSidebar from "../Components/Sidebar/MobileSidebar";
import { useMediaQuery } from '@mui/system';
import { apiUrlBase, apiUrlPrefix } from "../../../config";
import * as signalR from "@microsoft/signalr";
import extractNextChatMessages from "../helper-methods/fetchNextMessages";

const ChatComponent = ({ communityId }) => {
  const { currentCommunityId } = useStore(); // Use zustand store
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState("");
  const [inputEmotion, setInputEmotion] = useState("");
  const [users, setUsers] = useState([]);
  const [userList, setUserList] = useState([]);
  const [chatAdmins, setChatAdmins] = useState([]);
  const [mobileUserList, setMobileUserList] = useState(false);
  const { setDiscussions, chatId } = useStore();
  const messagesEndRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const [isReady, setIsReady] = React.useState(false);
  const [connection, setConnection] = useState(null);
  const [message, setMessage] = useState(0);
  const topObserver = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadTime = useRef(0);
  const [sentMessage, setSentMessage] = useState(false);

  //This is used to translate the emotion into a specific colour from the backend e.g.: Happy to #FFDF00 if no value is given then it is neutral
  const emotionStyles = {
    Happy: "#FFDF00",
    Sad: "#458ED7",
    Annoyed: "#ADFF2F",
    Angry: "#FF0000",
    Neutral: "grey.200"
  };

  const loadMore = async () => {
    if (isLoading) return; // Don't trigger if already fetching

    setIsLoading(true);

    fetchMessages();

    setIsLoading(false);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const now = Date.now();
        const waitTime = 2000;
        // If the top sentinel is visible, load the next 30
        if (entries[0].isIntersecting && (now - lastLoadTime.current) > waitTime) {
          lastLoadTime.current = now;
          loadMore();
        }
      },
      { threshold: 1.0 }
    );

    if (topObserver.current) {
      observer.observe(topObserver.current);
    }

    return () => observer.disconnect();
  }, [loadMore]);

  const fetchMessages = async () => {
    var chatMessages = ""
    if (message < 30 || sentMessage) {
      chatMessages = await extractChatMessages(chatId ? chatId : '69769ec2f601532a29c0a0a3', currentCommunityId);
      setSentMessage(false);
    } /*else {
      chatMessages = await extractNextChatMessages(chatId ? chatId : '69769ec2f601532a29c0a0a3', currentCommunityId, message);
    }*/
    if (chatMessages) {
      setMessage((prevIndex) => prevIndex + 30);
      setChatAdmins(chatMessages.discussions[0].admins);
      setUserList(chatMessages.users);
      setUsers(chatMessages.discussions[0].users.slice(0, 50)); // Makes it so all the users in the specific chat are defined here up to 50 users
      setTitle(chatMessages.discussions[0].title); // Makes it so all the users in the specific chat are defined here

      // Extract and map messages to include username and content
      const extractedMessages = chatMessages.discussions.flatMap((chat) =>
        chat.messages.map((message) => ({
          username: message.username ?? "",
          content: message.content ?? "",
          emotion: message.emotion ?? "Neutral",
          created: message.created ?? ""
        }))
      );

      // Merge with current local messages, avoiding duplicates
      setMessages((prevMessages) => {
        //This is to resolve an issue when choosing a new discussion to resolve the issue of a user choosing a new discussion so the messages of the discussion they just left do not appear at the top 
        if (prevMessages.length > 0) {
          if (prevMessages.length != extractedMessages.length || extractedMessages.length <= 0 || (prevMessages[0].content != extractedMessages[0].content && prevMessages[0].username != extractedMessages[0].username)) {
            prevMessages = [];
          }
        }
        const combinedMessages = [...prevMessages, ...extractedMessages];
        const uniqueMessages = Array.from(
          new Map(
            combinedMessages.map((msg) => [msg.content + msg.username, msg])
          ).values()
        );
        return uniqueMessages;
      });
    }
  };

  const sortedUsers = React.useMemo(() => {
    // Create a copy first ([...users]) because .sort() mutates the original array
    return [...users].sort((a, b) => {
      const isAAdmin = chatAdmins?.includes(a.id);
      const isBAdmin = chatAdmins?.includes(b.id);

      if (isAAdmin && !isBAdmin) return -1;
      if (!isAAdmin && isBAdmin) return 1;

      // Use name or title depending on your object structure
      const nameA = a.name || a.title || "";
      const nameB = b.name || b.title || "";

      return nameA.localeCompare(nameB);
    });
  }, [users, chatAdmins]); // Only re-sort if these change

  useEffect(() => {
    fetchMessages();
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrlBase}chatHub`)
      .withAutomaticReconnect()
      .build();

    // 1. Create a variable to track if this specific effect is still "alive"
    let isMounted = true;

    const startConnection = async () => {
      try {
        if (newConnection.state === signalR.HubConnectionState.Disconnected) {
          await newConnection.start();

          if (isMounted) {
            console.log("Connected to SignalR!");
            // Join the group after connecting
            await newConnection.invoke("JoinDiscussion", chatId);

            newConnection.on("NewMessageReceived", () => {
              fetchMessages();
            });
          }
        }
      } catch (err) {
        console.error("SignalR Connection Error: ", err);
      }
    };

    startConnection();



    return () => {
      isMounted = false;
      if (newConnection) {
        newConnection.stop();
        console.log("SignalR Connection Stopped");
      }
    };
  }, [chatId]);

  useEffect(() => {

    const timeout = 100;
    // 1. Create a small delay to let the DOM settle
    const timer = setTimeout(() => {

      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });

      requestAnimationFrame(() => {
        setIsReady(true);
      });

    }, timeout); // 100ms is usually the "sweet spot" for PWAs

    return () => clearTimeout(timer); // Cleanup


  }, []);

  useEffect(() => {
    if (connection) {
      connection.start()
        .then(() => {
          console.log("Connected to SignalR!");

          connection.invoke("JoinDiscussion", chatId)
            .catch(err => console.error("Join Group Error: ", err));

          connection.on("NewMessageReceived", () => {
            console.log("Signal received! Fetching fresh data...");
            fetchMessages();
          });

        })
        .catch(err => console.error("Connection Error: ", err));
    }
  }, [connection, chatId]);

  // 2. Scroll to bottom when messages are added
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

      // Only auto-scroll if the user is already near the bottom
      if (distanceToBottom < 150) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages.length]);


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <HeaderBar isLoggedIn={true} />
      <Box sx={{ display: 'flex', flexDirection: 'row', width: '100vw' }}>
        <Sidebar />
        <MobileSidebar />
        <Box sx={{
          display: 'flex', flexDirection: 'column', height: { xs: '94vh', md: '95vh' }, width: { xs: '100vw', md: '80vw' },
        }}>
          <Box sx={{ ml: 1, justifyContent: 'center', alignItems: 'center', zIndex: 1 }}>
            <ChatHeader mobileUserList={mobileUserList} setMobileUserList={setMobileUserList} title={title} />

            {mobileUserList && (<MobileUserListComponent chatId={chatId}
              setDiscussions={setDiscussions} sortedUsers={sortedUsers} chatAdmins={chatAdmins} userList={userList} />)}
          </Box>
          {/* HORIZONTAL WRAPPER */}
          {/* Chat messages */}
          <Box ref={scrollContainerRef}
            onLoad={() => {
              // If the image loads and we are near the bottom, snap to bottom
              messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
            }}
            sx={{
              flexBasis: "75%", // This sets the starting width
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              mb: 2,
              aspectRatio: '1 / 1',
              height: "90%",
              maxHeight: "90%",
              opacity: isReady ? 1 : 0
            }}
          >

            <div ref={topObserver} style={{ height: '10px' }} />

            <ChatMessage messages={messages} userList={userList} emotionStyles={emotionStyles} chatId={chatId} communityId={communityId} 
            setMessages={setMessages} chatAdmins={chatAdmins}/>

            {/* THIS IS YOUR ANCHOR */}
            <div ref={messagesEndRef} />

          </Box>

          <MobileEmojiStack emotionStyles={emotionStyles} setInputEmotion={setInputEmotion} />

          <InputBoxComponent emotionStyles={emotionStyles} inputEmotion={inputEmotion} setInputEmotion={setInputEmotion} chatId={chatId}
            fetchMessages={fetchMessages} setMessages={setMessages} setSentMessage={setSentMessage} />
        </Box>
        {/* 
            Users list component PC
        */}
        <DesktopUserListComponent sx={{ margin: 0, padding: 0 }} chatId={chatId} setDiscussions={setDiscussions} sortedUsers={sortedUsers}
          userList={userList} chatAdmins={chatAdmins} chatTitle={title} />
      </Box>
    </Box>
  );
};

export default ChatComponent;